import type { CampaignReportData } from '@elva/ui';
import type {
  CrAdVariant,
  CrDayBooking,
  CrFunnelStage,
  CrHeroMetric,
  CrKeywordRow,
  CrLogEntry,
  CrLogType,
  CrPatientValue,
  CrPeakHour,
} from '@elva/ui';
import { growthGet, growthGetBlob, growthPost } from '@/data/http';
import { downloadBlob } from '@/data/download';
import type { BeCampaignReport, BeCampaignReportPayload } from '@/data/marketingApiTypes';
import { deepCamel, num, str } from '@/data/adapt';
import { enumLower } from '@/data/adapt';

function unwrapReportPayload(be: BeCampaignReport): BeCampaignReportPayload {
  if (be.report && typeof be.report === 'object') return be.report;
  return be;
}

function centsToDollars(cents: number | null | undefined): number {
  if (cents == null || !Number.isFinite(cents)) return 0;
  return Math.round(cents / 100);
}

function adaptHeroMetric(row: Record<string, unknown>): CrHeroMetric {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    label: str(c.label),
    value: str(c.value),
    sub: str(c.sub, ''),
    positive: Boolean(c.positive),
    subPositive: Boolean(c.subPositive),
  };
}

function adaptPerformanceHeroMetrics(
  performance: BeCampaignReportPayload['performance'],
): CrHeroMetric[] {
  if (!performance) return [];
  const spend = centsToDollars(performance.current_month_spend_cents);
  const leads = num(performance.last_7d_leads);
  const cpl = centsToDollars(performance.last_7d_cost_per_lead_cents);
  const roi = str(performance.last_7d_roi_pct, '0');

  return [
    {
      label: 'Spend (MTD)',
      value: `$${spend.toLocaleString()}`,
      sub: 'Current month',
      positive: spend > 0,
      subPositive: spend > 0,
    },
    {
      label: 'Leads (7d)',
      value: String(leads),
      sub: 'Last 7 days',
      positive: leads > 0,
      subPositive: leads > 0,
    },
    {
      label: 'Cost / lead (7d)',
      value: cpl > 0 ? `$${cpl.toLocaleString()}` : '—',
      sub: '7-day window',
      positive: cpl > 0,
      subPositive: false,
    },
    {
      label: 'ROI (7d)',
      value: `${roi}%`,
      sub: 'Last 7 days',
      positive: num(roi) > 0,
      subPositive: num(roi) > 0,
    },
  ];
}

function adaptFunnelStage(row: Record<string, unknown>): CrFunnelStage {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    id: str(c.id),
    label: str(c.label),
    value: num(c.value),
    note: str(c.note, ''),
    width: num(c.width, 50),
    tone: (enumLower(c.tone as string, 'neutral') as CrFunnelStage['tone']) ?? 'neutral',
    isLast: Boolean(c.isLast),
  };
}

function mapKeywordStatus(raw: string | undefined): CrKeywordRow['status'] {
  const key = (raw ?? 'unknown').toLowerCase();
  if (key === 'paused') return 'paused';
  if (key === 'active' || key === 'good') return 'good';
  return 'watch';
}

function adaptKeywordRow(row: Record<string, unknown>): CrKeywordRow {
  const c = deepCamel<Record<string, unknown>>(row);
  const matchRaw = str(c.matchType ?? c.match, 'phrase');
  const match =
    matchRaw.charAt(0).toUpperCase() + matchRaw.slice(1).toLowerCase();
  return {
    keyword: str(c.keyword),
    match,
    impressions: num(c.impressions),
    clicks: num(c.clicks),
    cpc: num(c.cpc),
    bookings: num(c.bookings),
    costPerBooking: centsToDollars(num(c.costPerBookingCents ?? c.costPerBooking)),
    status: mapKeywordStatus(str(c.status ?? c.statusInAds)),
  };
}

function adaptAdVariant(row: Record<string, unknown>): CrAdVariant {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    label: str(c.label ?? c.variantLabel),
    winner: Boolean(c.winner ?? c.isWinner),
    headline: str(c.headline),
    description: str(c.description),
    impressions: num(c.impressions),
    clicks: num(c.clicks),
    ctr: num(c.ctr),
    bookings: num(c.bookings),
    costPerBooking: centsToDollars(num(c.costPerBookingCents ?? c.costPerBooking)),
    share: str(c.share, '—'),
  };
}

function adaptLogEntry(row: Record<string, unknown>): CrLogEntry {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    date: str(c.date),
    action: str(c.action),
    reason: str(c.reason, '—'),
    impact: str(c.impact, '—'),
    type: (enumLower(c.type as string, 'launch') as CrLogType) ?? 'launch',
  };
}

export function adaptCampaignReport(be: BeCampaignReport): CampaignReportData {
  const payload = unwrapReportPayload(be);
  const data: CampaignReportData = {};

  if (payload.hero_metrics?.length) {
    data.heroMetrics = payload.hero_metrics.map(adaptHeroMetric);
  } else if (payload.performance) {
    data.heroMetrics = adaptPerformanceHeroMetrics(payload.performance);
  }

  if (payload.funnel?.length) data.funnel = payload.funnel.map(adaptFunnelStage);
  if (payload.bookings_by_day?.length) {
    data.bookingsByDay = payload.bookings_by_day.map((row) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return { day: str(c.day), value: num(c.value) } satisfies CrDayBooking;
    });
  }
  if (payload.peak_hours?.length) {
    data.peakHours = payload.peak_hours.map((row) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return { slot: str(c.slot), value: num(c.value), note: str(c.note, '') } satisfies CrPeakHour;
    });
  }
  if (payload.patient_value?.length) {
    data.patientValue = payload.patient_value.map((row) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return { label: str(c.label), value: str(c.value), sub: str(c.sub, '') } satisfies CrPatientValue;
    });
  }
  if (payload.keywords?.length) data.keywords = payload.keywords.map(adaptKeywordRow);
  if (payload.ad_variants?.length) {
    data.adVariants = payload.ad_variants.map(adaptAdVariant);
  } else if (payload.lp_variants?.length) {
    data.adVariants = payload.lp_variants.map(adaptAdVariant);
  }
  if (payload.log?.length) data.log = payload.log.map(adaptLogEntry);
  return data;
}

export async function fetchCampaignReport(campaignId: string): Promise<CampaignReportData> {
  const be = await growthGet<BeCampaignReport>(`/campaigns/${campaignId}/report/`);
  return adaptCampaignReport(be);
}

export async function pauseCampaignFromReport(campaignId: string) {
  return growthPost(`/campaigns/${campaignId}/pause/`);
}

export async function exportCampaignReport(campaignId: string): Promise<void> {
  const blob = await growthGetBlob(`/campaigns/${campaignId}/report/export/`, { format: 'pdf' });
  downloadBlob(blob, `campaign-${campaignId}-report.pdf`);
}
